
import { format } from 'date-fns';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

interface Estimate {
  id: string;
  estimate_number: string;
  title: string;
  status: string;
  created_at: string;
  total_amount?: number;
  expires_at?: string;
}

interface CustomerEstimatesProps {
  customerId: string;
}

const CustomerEstimates: React.FC<CustomerEstimatesProps> = ({ customerId }) => {
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEstimates();
  }, [customerId]);

  const fetchEstimates = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('estimates')
        .select(`
          id,
          estimate_number,
          title,
          status,
          created_at,
          expires_at
        `)
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching estimates:', error);
        toast.error('Failed to load estimates');
        return;
      }

      // Calculate total amounts for each estimate
      const estimatesWithTotals = await Promise.all(
        (data || []).map(async (estimate) => {
          const { data: lineItems } = await supabase
            .from('estimate_line_items')
            .select('total_cost_snapshot, manual_override, manual_total_cost')
            .eq('estimate_id', estimate.id);

          const totalAmount = lineItems?.reduce((sum, item) => {
            const cost = item.manual_override && item.manual_total_cost 
              ? item.manual_total_cost 
              : item.total_cost_snapshot;
            return sum + (cost || 0);
          }, 0) || 0;

          return {
            ...estimate,
            total_amount: totalAmount
          };
        })
      );

      setEstimates(estimatesWithTotals);
    } catch (error) {
      console.error('Error fetching estimates:', error);
      toast.error('Failed to load estimates');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'secondary';
      case 'sent':
        return 'default';
      case 'approved':
        return 'default';
      case 'rejected':
        return 'destructive';
      case 'expired':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const navigate = useNavigate();

  const handleViewEstimate = (estimateId: string) => {
    navigate(`/estimates/${estimateId}`);
  };

  const handleCreateEstimate = () => {
    navigate(`/estimates/create?customer=${customerId}`);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Estimates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <p>Loading estimates...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Estimates ({estimates.length})
        </CardTitle>
        <Button onClick={handleCreateEstimate} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          New Estimate
        </Button>
      </CardHeader>
      <CardContent>
        {estimates.length > 0 ? (
          <div className="space-y-3">
            {estimates.map((estimate) => (
              <div
                key={estimate.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{estimate.estimate_number || 'Draft'}</span>
                    <Badge variant={getStatusColor(estimate.status)}>
                      {estimate.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">{estimate.title}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Created: {format(new Date(estimate.created_at), 'dd MMMM yyyy')}</span>
                    {estimate.expires_at && (
                      <span>Expires: {format(new Date(estimate.expires_at), 'dd MMMM yyyy')}</span>
                    )}
                    {estimate.total_amount !== undefined && (
                      <span className="font-medium text-foreground">
                        {formatCurrency(estimate.total_amount)}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleViewEstimate(estimate.id)}
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-20" />
            <p>No estimates found for this customer</p>
            <Button 
              onClick={handleCreateEstimate} 
              variant="outline" 
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Estimate
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerEstimates;
